import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { $declinedAuthStore, $minimalBalanceStore, $usersListStore } from '@lib/store';
import { cn, ValidateUtils } from '@shared/utils';
import { AlertTypeEnum, RecipientChannel } from '@shared/api';
import { BaseInput, Button, Modal, Radio, Select, Text } from '@shared/widgets';

const RecipientType = {
  OS: 'OS User',
  Other: 'Other',
} as const;
type RecipientType = (typeof RecipientType)[keyof typeof RecipientType];
const USER_TYPE_OPTIONS = [
  { text: 'sidebar.modals.addRecipient.userType.os', value: RecipientType.OS },
  { text: 'sidebar.modals.addRecipient.userType.other', value: RecipientType.Other },
];

interface IForm {
  user: string;
  name: string;
  email: string;
}
const DEFAULT_VALUES: IForm = { user: '', name: '', email: '' };

interface AddRecipientModalProps {
  show: boolean;
  onClose: () => void;
  alertType: AlertTypeEnum;
}

export const AddRecipientModal = (props: AddRecipientModalProps) => {
  const usersListStore = useUnit($usersListStore.store);
  const minimalBalanceStore = useUnit($minimalBalanceStore.store);
  const declinedAuthStore = useUnit($declinedAuthStore.store);

  const [userType, setUserType] = useState<RecipientType>(RecipientType.OS);

  const { control, formState, handleSubmit, setValue, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const { t } = useTranslation('account');

  useEffect(() => {
    if (!props.show) return;
    $usersListStore.events.getUsers({ Count: 100 });
  }, [props.show]);

  const closeHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
    setUserType(RecipientType.OS);
  };
  const switchUserTypeHandler = (type: RecipientType) => {
    reset(DEFAULT_VALUES);
    setUserType(type);
  };
  const submitHandler = (data: IForm) => {
    if (props.alertType === AlertTypeEnum.minimalBalance)
      $minimalBalanceStore.events.add({ firstName: data.name, email: data.email, userID: data.user || null, channel: RecipientChannel.Email });
    else
      $declinedAuthStore.events.addRecipient({ firstName: data.name, email: data.email, userID: data.user || null, channel: RecipientChannel.Email });
    closeHandler();
  };

  const userList = useMemo(() => {
    switch (props.alertType) {
      case AlertTypeEnum.minimalBalance:
        return usersListStore.data
          .filter((user) => !minimalBalanceStore.recipient.find((recipient) => recipient.email === user.emailAddress))
          .map((user) => ({ value: user.id, label: `${user.firstName || ''} ${user.lastName || ''}` }));
      case AlertTypeEnum.declinedAuth:
        return usersListStore.data
          .filter((user) => !declinedAuthStore.recipient.find((recipient) => recipient.email === user.emailAddress))
          .map((user) => ({ value: user.id, label: `${user.firstName || ''} ${user.lastName || ''}` }));
      default:
        return usersListStore.data.map((user) => ({ value: user.id, label: `${user.firstName || ''} ${user.lastName || ''}` }));
    }
  }, [props.alertType, usersListStore.data, minimalBalanceStore.recipient, declinedAuthStore.recipient]);
  return (
    <Modal isOpen={props.show} onClose={closeHandler} title={t('sidebar.modals.addRecipient.title')}>
      <form className='flex flex-col gap-5' noValidate onSubmit={handleSubmit(submitHandler)}>
        <div>
          <Text className='mb-2'>{t('sidebar.modals.addRecipient.placeholders.userType')}</Text>
          <Radio
            orientation='horizontal'
            options={USER_TYPE_OPTIONS.map((option) => ({ ...option, text: t(option.text) }))}
            value={userType}
            onChange={(value) => switchUserTypeHandler(value as RecipientType)}
          />
        </div>
        {userType === RecipientType.OS && (
          <Controller
            name='user'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field, fieldState: { error } }) => (
              <Select
                label={t('sidebar.modals.addRecipient.placeholders.name')}
                nullValue={{ isEnable: false, text: t('placeholders.selectRecipient', { ns: 'common' }) }}
                items={userList}
                required
                error={error?.message}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e);
                  const user = usersListStore.data.find((user) => user.id === e)!;
                  setValue('name', `${user.firstName} ${user.lastName}`);
                  setValue('email', user.emailAddress);
                }}
              />
            )}
          />
        )}
        {userType === RecipientType.Other && (
          <Controller
            name='name'
            control={control}
            rules={{ required: t('errors.form.required', { ns: 'common' }) }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput
                {...field}
                label={t('sidebar.modals.addRecipient.placeholders.nameAndSurname')}
                placeholder={t('sidebar.modals.addRecipient.placeholders.nameAndSurname')}
                required
                error={error?.message}
              />
            )}
          />
        )}
        <div>
          <Controller
            name='email'
            control={control}
            rules={{
              required: t('errors.form.required', { ns: 'common' }),
              pattern: { value: ValidateUtils.emailRegex, message: t('errors.form.email.invalidFormat', { ns: 'common' }) },
              validate: (value) => {
                const match =
                  (props.alertType === AlertTypeEnum.declinedAuth && declinedAuthStore.recipient.find((recipient) => recipient.email === value)) ||
                  (props.alertType === AlertTypeEnum.minimalBalance && minimalBalanceStore.recipient.find((recipient) => recipient.email === value));
                if (match) return t('sidebar.modals.addRecipient.userError');
                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <BaseInput
                {...field}
                className={cn(userType === RecipientType.OS && 'pointer-events-none opacity-60')}
                label={t('placeholders.email', { ns: 'common' })}
                placeholder={t('placeholders.email', { ns: 'common' })}
                required
                error={error?.message}
              />
            )}
          />
        </div>
        <Button className='mt-7' fullWidth color='primary' type='submit' disabled={formState.isSubmitting}>
          {t('sidebar.modals.addRecipient.buttons.addRecipient')}
        </Button>
      </form>
    </Modal>
  );
};
